import React from 'react';
import {
  Badge,
  BlockStack,
  Box,
  Button,
  InlineStack,
  Pagination,
  Spinner,
  Text
} from '@shopify/polaris';
import { RefreshIcon, ReplayIcon, PauseCircleIcon } from '@shopify/polaris-icons';
import { flowStatusApi } from '../../services/api/flowStatusApi';

function badgeTone(status) {
  if (status === 'completed') return 'success';
  if (status === 'retry_scheduled') return 'attention';
  if (status === 'failed_terminal' || status === 'blocked') return 'critical';
  if (status === 'manual_hold') return 'warning';
  return 'info';
}

function formatDate(value) {
  if (!value) return 'Not set';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? 'Not set' : date.toLocaleString();
}

function compactIds(externalIds = {}) {
  return Object.entries(externalIds)
    .filter(([, value]) => value)
    .slice(0, 4)
    .map(([key, value]) => `${key}: ${value}`);
}

const ACTION_STATUSES = new Set(['retry_scheduled', 'failed_terminal', 'manual_hold', 'blocked']);
const PAGE_SIZE = 10;
const MAX_PAGES = 5;

export function FlowStatusPanel({ service, embedded = false }) {
  const [runs, setRuns] = React.useState([]);
  const [page, setPage] = React.useState(1);
  const [totalRuns, setTotalRuns] = React.useState(0);
  const [loading, setLoading] = React.useState(false);
  const [actionLoading, setActionLoading] = React.useState(null);
  const [error, setError] = React.useState(null);

  const loadRuns = React.useCallback(async () => {
    if (!service) return;
    setLoading(true);
    setError(null);
    try {
      const resp = await flowStatusApi.getRuns({
        service,
        page,
        limit: PAGE_SIZE,
        status: Array.from(ACTION_STATUSES).join(',')
      });
      setRuns(resp?.data || []);
      setTotalRuns(resp?.pagination?.total || 0);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [service, page]);

  React.useEffect(() => {
    loadRuns();
    const timer = setInterval(loadRuns, 15000);
    return () => clearInterval(timer);
  }, [loadRuns]);

  const handleRetry = async (run) => {
    setActionLoading(run._id);
    try {
      const detail = await flowStatusApi.getRun(run._id);
      const step = (detail?.data?.steps || []).find((item) => item.status === 'retryable_failed')
        || (detail?.data?.steps || []).find((item) => item.stepKey === run.currentStepKey);
      if (step?._id) await flowStatusApi.retryStep(step._id);
      await loadRuns();
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(null);
    }
  };

  const handleHold = async (run) => {
    setActionLoading(run._id);
    try {
      const detail = await flowStatusApi.getRun(run._id);
      const step = (detail?.data?.steps || []).find((item) => item.status === 'retryable_failed')
        || (detail?.data?.steps || []).find((item) => item.stepKey === run.currentStepKey);
      if (step?._id) await flowStatusApi.holdStep(step._id, 'Held from Flow Status panel');
      await loadRuns();
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(null);
    }
  };

  React.useEffect(() => {
    setPage(1);
  }, [service]);

  const totalPages = Math.min(MAX_PAGES, Math.max(1, Math.ceil(totalRuns / PAGE_SIZE)));
  const safePage = Math.min(page, totalPages);

  const content = (
      <BlockStack gap="loose">
        <InlineStack align="space-between" blockAlign="center">
          <Text variant={embedded ? 'headingSm' : 'headingMd'} fontWeight="bold">Latest 50 retries and holds</Text>
          <InlineStack gap="base" blockAlign="center">
            {loading && <Spinner size="small" />}
            <Button icon={RefreshIcon} variant="tertiary" onClick={loadRuns} disabled={loading}>
              Refresh
            </Button>
          </InlineStack>
        </InlineStack>

        {error && (
          <Box padding="300" background="bg-surface-critical" borderRadius="100">
            <Text tone="critical">{error}</Text>
          </Box>
        )}

        {!loading && runs.length === 0 && (
          <Text tone="subdued">No retryable or held flow errors.</Text>
        )}

        <BlockStack gap="base">
          {runs.map((run) => {
            const ids = compactIds(run.externalIds);
            const retryable = run.status === 'retry_scheduled';
            return (
              <Box key={run._id} padding="300" background="bg-surface-secondary" borderRadius="100">
                <BlockStack gap="tight">
                  <InlineStack align="space-between" blockAlign="center">
                    <InlineStack gap="base" blockAlign="center">
                      <Badge tone={badgeTone(run.status)}>{run.status}</Badge>
                      <Text fontWeight="semibold">{run.sourceDisplayId || run.sourceId || run.idempotencyKey}</Text>
                    </InlineStack>
                    <InlineStack gap="base">
                      <Button
                        size="slim"
                        icon={ReplayIcon}
                        disabled={!retryable || actionLoading === run._id}
                        onClick={() => handleRetry(run)}
                      >
                        Retry
                      </Button>
                      <Button
                        size="slim"
                        icon={PauseCircleIcon}
                        disabled={actionLoading === run._id || run.status === 'completed'}
                        onClick={() => handleHold(run)}
                      >
                        Hold
                      </Button>
                    </InlineStack>
                  </InlineStack>
                  <Text tone="subdued">Step: {run.currentStepKey || 'Not set'} | Next retry: {formatDate(run.retry?.nextRetryAt)}</Text>
                  {run.lastError?.message && (
                    <Text tone="critical">{run.lastError.message}</Text>
                  )}
                  {ids.length > 0 && (
                    <Text tone="subdued">{ids.join(' | ')}</Text>
                  )}
                  {run.links?.n8nExecutionIds?.length > 0 && (
                    <Text tone="subdued">n8n: {run.links.n8nExecutionIds.join(', ')}</Text>
                  )}
                </BlockStack>
              </Box>
            );
          })}
        </BlockStack>
        {totalPages > 1 && (
          <InlineStack align="space-between" blockAlign="center">
            <Text variant="bodySm" tone="subdued">
              Showing page {safePage} of {totalPages}, 10 records per page
            </Text>
            <Pagination
              label={`Retry page ${safePage} of ${totalPages}`}
              hasPrevious={safePage > 1}
              onPrevious={() => setPage((current) => Math.max(1, current - 1))}
              hasNext={safePage < totalPages}
              onNext={() => setPage((current) => Math.min(totalPages, current + 1))}
            />
          </InlineStack>
        )}
      </BlockStack>
  );

  if (embedded) return content;
  return (
    <Box padding="400" borderColor="border" borderWidth="025" borderRadius="200">
      {content}
    </Box>
  );
}
